import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateSpvPool,
  useSpvPool,
  useUpdateSpvPool,
} from "@/hooks/capitalMarkets/useSpvPools";
import { useSpvTrustList } from "@/hooks/capitalMarkets/useSpvTrusts";
import type { SpvPoolCreate } from "@/types/spvPool";

const EMPTY_FORM: SpvPoolCreate = {
  spvId: "",
  poolName: "",
  inceptionDate: "",
  portfolioId: null,
  targetPoolSize: 0,
  minSegmentCount: 0,
  maxBorrowerConcentrationPct: 0.25,
  weightedAvgCreditRatingTarget: "",
  offeringType: "",
  notes: "",
};

export function PoolFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  const poolQuery = useSpvPool(id);
  const trustsQuery = useSpvTrustList({ pageSize: 200 });
  const create = useCreateSpvPool();
  const update = useUpdateSpvPool(id ?? "");

  const [form, setForm] = useState<SpvPoolCreate>(EMPTY_FORM);

  useEffect(() => {
    if (isEdit && poolQuery.data) {
      const p = poolQuery.data;
      setForm({
        spvId: p.spvId,
        poolName: p.poolName,
        inceptionDate: p.inceptionDate?.slice(0, 10) ?? "",
        portfolioId: p.portfolioId,
        targetPoolSize: p.targetPoolSize,
        minSegmentCount: p.minSegmentCount,
        maxBorrowerConcentrationPct: p.maxBorrowerConcentrationPct,
        weightedAvgCreditRatingTarget: p.weightedAvgCreditRatingTarget,
        offeringType: p.offeringType,
        notes: p.notes ?? "",
      });
    }
  }, [isEdit, poolQuery.data]);

  const trusts = useMemo(() => trustsQuery.data?.data ?? [], [trustsQuery.data]);

  const set = <K extends keyof SpvPoolCreate>(k: K, v: SpvPoolCreate[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const pending = create.isPending || update.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const action = isEdit
      ? update.mutateAsync(form)
      : create.mutateAsync(form);
    action
      .then((pool) => {
        toast.success(isEdit ? "Pool updated." : "Pool created.");
        navigate(`/capital-markets/pools/${pool.id}`);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title={isEdit ? "Edit SPV Pool" : "New SPV Pool"}
        description="Define the pool parameters. Composition and IDUP are managed from the detail page."
      />
      <form onSubmit={handleSubmit} className="flex flex-col gap-space-4">
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <FormField label="Pool name" required>
              <Input
                value={form.poolName}
                onChange={(e) => set("poolName", e.target.value)}
                required
              />
            </FormField>
            <FormField label="SPV trust" required>
              <Select
                value={form.spvId}
                onChange={(e) => set("spvId", e.target.value)}
                required
              >
                <option value="">— Select trust —</option>
                {trusts.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.trustName}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Offering type" required>
              <Input
                value={form.offeringType}
                onChange={(e) => set("offeringType", e.target.value)}
                required
              />
            </FormField>
            <FormField label="Inception date" required>
              <Input
                type="date"
                value={form.inceptionDate}
                onChange={(e) => set("inceptionDate", e.target.value)}
                required
              />
            </FormField>
            <FormField label="Target pool size" required>
              <Input
                type="number"
                min={0}
                value={form.targetPoolSize}
                onChange={(e) =>
                  set("targetPoolSize", Number(e.target.value) || 0)
                }
                required
              />
            </FormField>
            <FormField label="Min segment count" required>
              <Input
                type="number"
                min={0}
                value={form.minSegmentCount}
                onChange={(e) =>
                  set("minSegmentCount", Number(e.target.value) || 0)
                }
                required
              />
            </FormField>
            <FormField
              label="Max borrower concentration"
              helperText="Decimal 0–1, e.g. 0.25 for 25%."
            >
              <Input
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={form.maxBorrowerConcentrationPct}
                onChange={(e) =>
                  set(
                    "maxBorrowerConcentrationPct",
                    Number(e.target.value) || 0,
                  )
                }
              />
            </FormField>
            <FormField label="Wtd avg credit rating target">
              <Input
                value={form.weightedAvgCreditRatingTarget}
                onChange={(e) =>
                  set("weightedAvgCreditRatingTarget", e.target.value)
                }
              />
            </FormField>
            <FormField label="Portfolio ID" className="md:col-span-2">
              <Input
                value={form.portfolioId ?? ""}
                onChange={(e) => set("portfolioId", e.target.value || null)}
              />
            </FormField>
            <FormField label="Notes" className="md:col-span-2">
              <Input
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value || null)}
              />
            </FormField>
          </div>
        </Card>
        <div className="flex items-center justify-end gap-space-2">
          <Button
            type="button"
            variant="text"
            onClick={() => navigate("/capital-markets/pools")}
          >
            Cancel
          </Button>
          <Button type="submit" loading={pending}>
            {isEdit ? "Save changes" : "Create pool"}
          </Button>
        </div>
      </form>
    </>
  );
}
